// One model version: what it is, how it did on each recording it never heard, its grain rules, trials on
// Noise Lab chapters (flags per grain, compared with the engineer's edits), and making it the live version.
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { askToConfirm } from "../components/confirm";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, downloadFile, downloadViaUrl } from "../api/client";
import type { LabModelDetail, LabModelResultRow, LabModelRun, LabSet, LabSetDetail } from "../api/types";
import { useMe } from "../lib/me";
import { LabTabs } from "../components/LabTabs";
import { pct, STATUS_LABEL } from "./NoiseLabModels";
import { backdropClose } from "../lib/backdrop";

const BASE = "/api/modules/noise_lab";
const GRAINS = ["Coarse", "Medium", "Fine"] as const;

function ResultsTable({ rows, summary }: { rows: LabModelResultRow[]; summary?: Partial<LabModelResultRow> }) {
  return (
    <div className="lab-tablewrap">
      <table className="table lab-table">
        <thead>
          <tr>
            <th>Recording it never heard</th>
            <th className="r">Length</th>
            <th className="r">Your edits</th>
            <th className="r" title="Share of your mouth-noise edits found when showing 5 / 10 / 20 flags a minute">Found at 5 · 10 · 20 flags/min</th>
            <th className="r" title="Of your clearer edits (20 dB or more above the noise floor), found at 10 flags a minute">Clear ones</th>
            <th className="r" title="When it has found half your edits: how many of its flags land on one, and how many flags a minute that takes">Flags on your edits</th>
            <th className="r" title="Of its 20 most confident flags, how many are on your edits">Top 20</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.file}>
              <td>{r.file}</td>
              <td className="r num">{r.minutes != null ? `${Math.round(r.minutes)} min` : "—"}</td>
              <td className="r num">{r.n_edits ?? "—"}</td>
              <td className="r num">{pct(r.recall_at_5_per_min)} · {pct(r.recall_at_10_per_min)} · {pct(r.recall_at_20_per_min)}</td>
              <td className="r num">{pct(r.clear_edit_recall_at_10_per_min)}</td>
              <td className="r num">{pct(r.precision_at_half_recall)}{r.flags_per_min_at_half_recall != null ? ` at ${Math.round(r.flags_per_min_at_half_recall)}/min` : ""}</td>
              <td className="r num">{r.edits_in_top_20 ?? "—"}</td>
            </tr>
          ))}
          {summary && rows.length > 1 && (
            <tr className="lab-median">
              <td>Median</td><td></td><td></td>
              <td className="r num">{pct(summary.recall_at_5_per_min)} · {pct(summary.recall_at_10_per_min)} · {pct(summary.recall_at_20_per_min)}</td>
              <td className="r num">{pct(summary.clear_edit_recall_at_10_per_min)}</td>
              <td className="r num">{pct(summary.precision_at_half_recall)}{summary.flags_per_min_at_half_recall != null ? ` at ${Math.round(summary.flags_per_min_at_half_recall)}/min` : ""}</td>
              <td></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function TrialRow({ run, modelId, onChange, picked, onPick }: {
  run: LabModelRun; modelId: string; onChange: () => void; picked: boolean; onPick: (e: ReactMouseEvent<HTMLInputElement>) => void;
}) {
  const nav = useNavigate();
  const [grain, setGrain] = useState<"coarse" | "medium" | "fine">("coarse");
  const [err, setErr] = useState<string | null>(null);
  const g = run.summary?.grains;
  const status = run.status === "done" ? "Done" : run.status === "failed" ? "Failed" : run.status === "running" ? "Scoring…" : "Waiting";
  const cls = run.status === "done" ? "lab-done" : run.status === "failed" ? "lab-failed" : run.status === "running" ? "lab-extracting" : "lab-idle";

  async function review() {
    setErr(null);
    try {
      const r = await api<{ id: string }>(`${BASE}/models/${modelId}/runs/${run.id}/review`, {
        method: "POST", body: JSON.stringify({ grain, only_untouched: !!run.summary?.evaluated }),
      });
      nav(`/admin/noise-lab/${run.set_id}/review/${r.id}`);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <>
      <tr>
        <td className="lab-pickcol">
          {run.status === "done" && (
            <input type="checkbox" checked={picked}
                   // the work happens on click, where the Shift key is; onChange keeps the input controlled
                   onChange={() => { /* see onClick */ }}
                   onClick={onPick}
                   // Shift-clicking in a table otherwise paints a text selection across the rows
                   onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
                   aria-label={`Pick ${run.stem ?? "this chapter"} for download`} />
          )}
        </td>
        <td>
          <div className="lab-stem">{run.stem ?? "?"}</div>
          <div className="muted lab-fname">{run.set_name}</div>
        </td>
        <td>
          <span className={`lab-pill ${cls}`}>{status}</span>
          {run.error && <div className="lab-err-line" title={run.error}>{run.error}</div>}
        </td>
        {GRAINS.map((name) => {
          const x = g?.[name];
          return (
            <td key={name} className="r num lab-trialcell">
              {x ? (
                <>
                  <div>{x.flags} <span className="muted">({x.per_min}/min)</span></div>
                  {x.before_ceiling != null && x.before_ceiling > x.flags && (
                    <div className="muted" title={`${x.before_ceiling} qualified; the per-minute ceiling kept the ${x.flags} strongest`}>
                      strongest of {x.before_ceiling}
                    </div>
                  )}
                  {run.summary?.evaluated && x.edits_in_scope != null && (
                    <div className="muted">found {x.found} of {x.edits_in_scope} · {pct(x.on_an_edit)} on an edit</div>
                  )}
                </>
              ) : "—"}
            </td>
          );
        })}
        <td className="r lab-actions">
          {run.status === "done" && (
            <>
              {GRAINS.map((name) => (
                <button key={name} className="btn ghost sm"
                        title={`Audition markers for the ${name} level. Each name starts with its rank (01 = strongest): sort by Name to go strongest first.`}
                        onClick={() => downloadViaUrl(`${BASE}/models/${modelId}/runs/${run.id}/files/markers_${name.toLowerCase()}.csv/url`).catch((e) => setErr((e as Error).message))}>
                  {name[0]}
                </button>
              ))}
              <select className="input lab-grainsel" value={grain} onChange={(e) => setGrain(e.target.value as typeof grain)} aria-label="Grain to review">
                <option value="coarse">Coarse</option><option value="medium">Medium</option><option value="fine">Fine</option>
              </select>
              <button className="btn ghost sm" onClick={review}
                      title={run.summary?.evaluated ? "Judge its markers on spots you left untouched, strongest first" : "Judge its markers, strongest first"}>
                {run.summary?.evaluated ? "Did I miss these?" : "Review flags"}
              </button>
            </>
          )}
          {run.status !== "running" && (
            <button className="btn ghost sm lab-danger" onClick={async () => { await api(`${BASE}/models/${modelId}/runs/${run.id}`, { method: "DELETE" }); onChange(); }}>Delete</button>
          )}
        </td>
      </tr>
      {err && <tr><td colSpan={7} className="lab-error">{err}</td></tr>}
    </>
  );
}

const GRAIN_HINT: Record<(typeof GRAINS)[number], string> = {
  Coarse: "fewest, the clearest noises: start here",
  Medium: "more, fainter ones too",
  Fine: "everything the model is sure of",
};

/** The Audition markers of the picked trials, at the levels ticked here, as one zip. */
function MarkersDialog({ modelId, runs, onClose }: { modelId: string; runs: LabModelRun[]; onClose: () => void }) {
  const [grains, setGrains] = useState<string[]>(["Coarse"]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const sets = [...new Set(runs.map((r) => r.set_name ?? ""))];
  const chapters = runs.map((r) => `${r.set_id}/${r.stem}`);
  const twice = new Set(chapters.filter((c, i) => chapters.indexOf(c) !== i)).size;
  const total = (g: (typeof GRAINS)[number]) => runs.reduce((n, r) => n + (r.summary?.grains?.[g]?.flags ?? 0), 0);
  const n = runs.length;

  async function download() {
    setBusy(true);
    setErr(null);
    try {
      await downloadFile(`${BASE}/models/${modelId}/markers.zip`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ run_ids: runs.map((r) => r.id), grains: GRAINS.filter((g) => grains.includes(g)).map((g) => g.toLowerCase()) }),
      });
      onClose();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" {...backdropClose(() => { if (!busy) onClose(); })}>
      <div className="modal-card lab-dl" role="dialog" aria-modal="true" aria-labelledby="lab-dl-title" onClick={(e) => e.stopPropagation()}>
        <h3 id="lab-dl-title">Download markers for {n} chapter{n === 1 ? "" : "s"}</h3>
        <p className="nid-lede">
          One Audition marker file per chapter for each level you tick, all in one zip
          {sets.length > 1 ? `, with a folder for each of the ${sets.length} sets` : sets[0] ? ` (${sets[0]})` : ""}. Open each
          file in the recording from <b>before</b> the edit, and sort by Name to go strongest first.
        </p>
        <div className="lab-dl-grains">
          {GRAINS.map((g) => (
            <label key={g}>
              <input type="checkbox" checked={grains.includes(g)}
                     onChange={(e) => setGrains((x) => (e.target.checked ? [...x, g] : x.filter((y) => y !== g)))} />
              <span><b>{g}</b> <span className="muted">· {total(g)} markers · {GRAIN_HINT[g]}</span></span>
            </label>
          ))}
        </div>
        {grains.length > 1 && <p className="nid-skipped">More than one level: each gets its own folder in the zip.</p>}
        {twice > 0 && (
          <p className="nid-skipped">
            {twice} chapter{twice === 1 ? " has" : "s have"} more than one trial picked. All are included; the newest keeps the plain name.
          </p>
        )}
        {err && <p className="dfd-warn">{err}</p>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" disabled={busy || !grains.length} onClick={download}>{busy ? "Preparing…" : "Download zip"}</button>
        </div>
      </div>
    </div>
  );
}

function TryIt({ modelId, onStarted }: { modelId: string; onStarted: () => void }) {
  const [setId, setSetId] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const sets = useQuery({ queryKey: ["lab-sets"], queryFn: () => api<{ sets: LabSet[] }>(`${BASE}/sets`) });
  const detail = useQuery({
    queryKey: ["lab-set", setId],
    queryFn: () => api<LabSetDetail>(`${BASE}/sets/${setId}`),
    enabled: !!setId,
  });
  useEffect(() => { setPicked([]); }, [setId]);
  const chapters = (detail.data?.pairs ?? []).filter((p) => p.before_uploaded);
  async function run() {
    setBusy(true);
    setErr(null);
    try {
      await api(`${BASE}/models/${modelId}/runs`, { method: "POST", body: JSON.stringify({ pair_ids: picked }) });
      setPicked([]);
      onStarted();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="lab-try">
      <select className="input" value={setId} onChange={(e) => setSetId(e.target.value)} aria-label="Training set">
        <option value="">Choose a set…</option>
        {(sets.data?.sets ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      {setId && (
        <div className="lab-try-chapters">
          <label className="lab-try-all">
            <input type="checkbox" checked={chapters.length > 0 && picked.length === chapters.length}
                   onChange={(e) => setPicked(e.target.checked ? chapters.map((p) => p.id) : [])} /> All chapters ({chapters.length})
          </label>
          {chapters.map((p) => (
            <label key={p.id}>
              <input type="checkbox" checked={picked.includes(p.id)}
                     onChange={(e) => setPicked((x) => (e.target.checked ? [...x, p.id] : x.filter((i) => i !== p.id)))} />
              {p.stem}{p.status === "ready" ? <span className="muted"> (just the recording: markers only, compared once you add the edited version)</span>
                : p.status !== "done" && <span className="muted"> (edits not extracted: markers only)</span>}
            </label>
          ))}
        </div>
      )}
      <button className="btn primary" disabled={!picked.length || busy} onClick={run}>
        {busy ? "Queuing…" : `Run on ${picked.length || ""} chapter${picked.length === 1 ? "" : "s"}`}
      </button>
      {err && <div className="lab-error">{err}</div>}
    </div>
  );
}

export function NoiseLabModel() {
  const { modelId = "" } = useParams();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [notes, setNotes] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [showDl, setShowDl] = useState(false);
  // where a Shift-range starts: the trial last ticked WITHOUT Shift. Kept as its id, not its row: a new
  // trial arrives at the top of the table and would move every row under it.
  const anchor = useRef<string | null>(null);
  const q = useQuery({
    queryKey: ["lab-model", modelId],
    queryFn: () => api<LabModelDetail>(`${BASE}/models/${modelId}`),
    enabled: !!me?.is_superadmin,
    refetchInterval: (qq) => (qq.state.data?.runs.some((r) => r.status === "queued" || r.status === "running") ? 5000 : false),
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["lab-model", modelId] }); qc.invalidateQueries({ queryKey: ["lab-models"] }); };

  async function act(what: "promote" | "retire" | "delete") {
    const m = q.data;
    if (!m) return;
    const ask = what === "promote" ? `Make "${m.name}" the live version? The live one now will be retired.`
      : what === "retire" ? `Retire "${m.name}"?` : `Delete "${m.name}" and its trials?`;
    const yes = what === "promote" ? "Make live" : what === "retire" ? "Retire" : "Delete";
    if (!(await askToConfirm(ask, { yes, danger: what === "delete" }))) return;
    try {
      await api(`${BASE}/models/${modelId}${what === "delete" ? "" : `/${what}`}`, { method: what === "delete" ? "DELETE" : "POST" });
      if (what === "delete") { qc.invalidateQueries({ queryKey: ["lab-models"] }); nav("/admin/noise-lab/models"); return; }
      refresh();
    } catch (e) {
      setMsg((e as Error).message);
    }
  }

  if (me && !me.is_superadmin) return <Navigate to="/" replace />;
  if (q.isError) return <div className="page lab-error">Couldn't load this model: {(q.error as Error).message}</div>;
  if (!q.data) return <div className="page muted">Loading…</div>;
  const m = q.data;
  const rows = m.results.files ?? [];
  const rules = m.grain_rules as Record<string, Record<string, number | boolean>> & { status?: string };
  const done = m.runs.filter((r) => r.status === "done");
  const pickedRuns = done.filter((r) => picked.includes(r.id));
  // "all" = the newest finished trial of each chapter (the runs arrive newest first)
  const newest = done.filter((r, i) => done.findIndex((x) => x.pair_id === r.pair_id) === i);
  const allPicked = newest.length > 0 && newest.every((r) => picked.includes(r.id));

  /** Ticking as in a file list: a plain click ticks one and anchors there, Shift+click does the same to every
   *  row between the anchor and here. Trials that aren't finished are stepped over, not a stop. */
  const pick = (index: number, e: ReactMouseEvent<HTMLInputElement>) => {
    const id = m.runs[index].id;
    const want = !picked.includes(id);
    const from = anchor.current ? m.runs.findIndex((r) => r.id === anchor.current) : -1;
    setPicked((x) => {
      const n = new Set(x);
      if (e.shiftKey && from >= 0) {
        const [lo, hi] = from < index ? [from, index] : [index, from];
        for (const r of m.runs.slice(lo, hi + 1)) {
          if (r.status !== "done") continue;
          if (want) n.add(r.id); else n.delete(r.id);
        }
      } else {
        if (want) n.add(id); else n.delete(id);
        anchor.current = id;
      }
      return [...n];
    });
  };
  const pickAll = (on: boolean) => {
    anchor.current = null;              // not a place to extend from: Shift would reach back past it
    setPicked(on ? newest.map((r) => r.id) : []);
  };

  return (
    <div className="page admin-page lab-page">
      <LabTabs />
      <div className="page-head">
        <div>
          <Link to="/admin/noise-lab/models" className="lab-back">Models</Link>
          <h1>{m.name} <span className={`lab-pill lab-model-${m.status} lab-h1pill`}>{STATUS_LABEL[m.status]}</span></h1>
          <div className="stat-chips">
            <span className="stat-chip">Trained {m.created || "—"}</span>
            <span className="stat-chip">{m.training.files?.length ?? 0} training files</span>
            <span className="stat-chip">{String(m.architecture.ensemble ?? 1)} cop{m.architecture.ensemble === 1 ? "y" : "ies"} averaged</span>
            {m.promoted_at && <span className="stat-chip">Live since {new Date(m.promoted_at).toLocaleDateString()}</span>}
          </div>
        </div>
        <div className="lab-head-actions">
          {m.status !== "live" && <button className="btn primary sm" onClick={() => act("promote")}>Make live</button>}
          {m.status === "live" && <button className="btn ghost sm" onClick={() => act("retire")}>Retire</button>}
          {m.status !== "live" && <button className="btn ghost sm lab-danger" onClick={() => act("delete")}>Delete</button>}
        </div>
      </div>
      {msg && <div className="lab-error">{msg}</div>}

      <section className="card lab-section">
        <p className="lab-desc">{m.description}</p>
        <textarea className="input lab-notes" rows={2} placeholder="Notes about this version"
                  value={notes ?? m.notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={async () => {
                    if (notes != null && notes !== m.notes) {
                      await api(`${BASE}/models/${modelId}`, { method: "PATCH", body: JSON.stringify({ notes }) });
                      refresh();
                    }
                  }} />
      </section>

      <section className="card lab-section">
        <div className="card-head">
          <div className="card-head-title">How it did on recordings it never heard</div>
          <div className="muted lab-add-hint">{m.results.protocol ?? ""}</div>
        </div>
        {rows.length ? <ResultsTable rows={rows} summary={m.results.summary} /> : <div className="empty-state">No held-out results came with this version.</div>}
      </section>

      {rules && rules.coarse && (
        <section className="card lab-section">
          <div className="card-head">
            <div className="card-head-title">Grain levels</div>
            <div className="muted lab-add-hint">{rules.status ?? ""}</div>
          </div>
          <ul className="lab-rules">
            <li><strong>Coarse:</strong> noises in open pauses no more than {Math.abs(Number(rules.coarse.min_severity_db))} dB below the voice that stick out
              by {String(rules.coarse.min_prominence_db)} dB or more, when the model is at least {Math.round(Number(rules.coarse.min_p) * 100)}% sure{rules.coarse.next_to_words ? "; noises next to words too" : "; noises right next to words are left out"}.</li>
            <li><strong>Medium:</strong> Coarse, plus pause noises no more than {Math.abs(Number(rules.medium.min_severity_db))} dB below the voice that stick out
              by {String(rules.medium.min_prominence_db)} dB{rules.medium.next_to_words ? ", and noises next to words" : ""}, at {Math.round(Number(rules.medium.min_p) * 100)}% or more.</li>
            <li><strong>Fine:</strong> everything the model is at least {Math.round(Number(rules.fine.min_p) * 100)}% sure of, however faint.</li>
          </ul>
        </section>
      )}

      <section className="card lab-section">
        <div className="card-head">
          <div className="card-head-title">Try it on chapters</div>
          <div className="muted lab-add-hint">
            Runs in the background, one chapter at a time. A processed chapter is compared with your edits; only one the
            model never learned from gives a fair test.
          </div>
        </div>
        <TryIt modelId={modelId} onStarted={refresh} />
        {done.length > 0 && (
          <div className="lab-bulk">
            <button className="btn sm" disabled={!pickedRuns.length} onClick={() => setShowDl(true)}>
              Download markers{pickedRuns.length ? ` for ${pickedRuns.length} chapter${pickedRuns.length === 1 ? "" : "s"}` : ""}…
            </button>
            {pickedRuns.length > 0
              ? <button className="btn ghost sm" onClick={() => pickAll(false)}>Clear</button>
              : <span className="muted">Tick finished chapters to download their markers in one go (Shift+click ticks a run of them).</span>}
          </div>
        )}
        {m.runs.length > 0 && (
          <div className="lab-tablewrap">
            <table className="table lab-table">
              <thead>
                <tr>
                  <th className="lab-pickcol">
                    <input type="checkbox" checked={allPicked} disabled={!newest.length}
                           onChange={(e) => pickAll(e.target.checked)}
                           title="Every finished chapter (its newest trial)" aria-label="Pick every finished chapter" />
                  </th>
                  <th>Chapter</th><th>Status</th>
                  {GRAINS.map((g) => <th key={g} className="r">{g}</th>)}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {m.runs.map((r, i) => (
                  <TrialRow key={r.id} run={r} modelId={modelId} onChange={refresh} picked={picked.includes(r.id)}
                            onPick={(e) => pick(i, e)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {showDl && pickedRuns.length > 0 && <MarkersDialog modelId={modelId} runs={pickedRuns} onClose={() => setShowDl(false)} />}
      </section>
    </div>
  );
}
